import "@/App.css";
import { Suspense, lazy, useEffect } from "react";
// Iter-231 — Install global JS error capture (Admin Roadmap §1.9).
// Must run once at app boot before any user code can throw.
import { installErrorCapture } from "./lib/errorCapture";

installErrorCapture();
import { BrowserRouter, Routes, Route, useLocation, useParams, Navigate } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { Toaster } from "sonner";
import { initWebVitals } from "./lib/webVitals";
import { captureAttribution } from "./lib/attribution";
import { bootstrapConsent } from "./lib/cookieConsent";
import CookieConsentBanner from "./components/CookieConsentBanner";
import Home from "./pages/Home";
import Header from "./components/Header";
import Footer from "./components/Footer";
import StickyCTA from "./components/StickyCTA";
import ExitIntentModal from "./components/ExitIntentModal";
import ScrollToTop from "./components/ScrollToTop";
import RouteAnalytics from "./components/RouteAnalytics";
import MobileStickyBar from "./components/MobileStickyBar";
import LiveSocialProof from "./components/LiveSocialProof";
import ReadingProgress from "./components/ReadingProgress";
import ErrorBoundary from "./components/ErrorBoundary";
import { ROUNDUPS } from "./lib/roundups";
import { COMPARISONS } from "./lib/comparisons";
import { QuoteModalProvider } from "./lib/quoteModal";
import QuoteModal from "./components/QuoteModal";
import { CompareStashProvider } from "./lib/compareStash";
import { StickyCompareDrawer, CompareDrawerModal } from "./components/CompareDrawer";
import MobileStickyCompareCTA from "./components/MobileStickyCompareCTA";
import RedirectGuard from "./components/RedirectGuard";
import { hydratePartnerRouting } from "./lib/partnerRouting";
import { hydrateFunnelCopy } from "./lib/categoryCopy";
import { hydrateStateOverrides } from "./lib/states";
import { hydrateSeoOverrides } from "./lib/seoOverrides";
import { useRevalidateOnCacheBump } from "./lib/cacheVersion";
import { Helmet } from "react-helmet-async";

/* ----- Lazy-loaded routes (code-split via React.lazy) -----
 * Everything below ships in its own webpack chunk so the homepage's
 * cold-cache TTI is dominated by Home + shell only. Each route's
 * code is fetched JIT on first navigation and cached by the browser.
 */
const ArticlePage = lazy(() => import("./pages/ArticlePage"));
const ArticlesIndex = lazy(() => import("./pages/ArticlesIndex"));
const GuidePage = lazy(() => import("./components/GuidePage"));
const AutoLoanCalculator = lazy(() => import("./pages/tools/AutoLoanCalculator"));
const RefinanceCalculator = lazy(() => import("./pages/tools/RefinanceCalculator"));
const AffordabilityCalculator = lazy(() => import("./pages/tools/AffordabilityCalculator"));
const InsuranceEstimator = lazy(() => import("./pages/tools/InsuranceEstimator"));
const TradeInValueEstimator = lazy(() => import("./pages/tools/TradeInValueEstimator"));
const TcoCalculator = lazy(() => import("./pages/tools/TcoCalculator"));
// Iter-276.F (Top-20 #19) — Canonical landing page for the
// VehicleClassTcoMatrix calculator embed.
const VehicleClassTcoPage = lazy(() => import("./pages/VehicleClassTcoPage"));
const CybercabCalculatorPage = lazy(() => import("./pages/CybercabCalculatorPage"));
const InsuranceIncreaseCalculator = lazy(() => import("./pages/tools/InsuranceIncreaseCalculator"));
// Iter-196.T — 5 new calculators (Lease vs Buy, Bi-weekly payoff,
// Title-tax-fee, Total interest paid, Insurance premium predictor)
// per the audit's missing-calculators backlog item. Lazy-loaded
// same as the existing 7 calculators to keep the initial bundle
// tight.
const LeaseVsBuyCalculator = lazy(() => import("./pages/tools/LeaseVsBuyCalculator"));
const BiWeeklyPaymentCalculator = lazy(() => import("./pages/tools/BiWeeklyPaymentCalculator"));
const TitleTaxFeeCalculator = lazy(() => import("./pages/tools/TitleTaxFeeCalculator"));
// Iter-418 — Standalone landing for the deductible break-even tool
// (previously in-article embed only).
const DeductibleBreakEvenPage = lazy(() => import("./pages/tools/DeductibleBreakEvenPage"));
const TotalInterestCalculator = lazy(() => import("./pages/tools/TotalInterestCalculator"));
const InsurancePremiumPredictor = lazy(() => import("./pages/tools/InsurancePremiumPredictor"));
const ExtendedWarrantyWorthIt = lazy(() => import("./pages/tools/ExtendedWarrantyWorthIt"));
// Iter-275 — Multi-vertical Savings Opportunity Score (competitive differentiator).
const SavingsOpportunityScore = lazy(() => import("./pages/tools/SavingsOpportunityScore"));
// Iter-274.G — Cluster A pillar / Tier-1 hub (closes the deep-link contract
// from iter-275.D state-page rail + InterClusterRail cluster card).
const BusinessUseInsuranceHub = lazy(() => import("./pages/BusinessUseInsuranceHub"));
// Iter-274.H — Cluster B pillar / Tier-1 hub (employer-side companion to the
// iter-274.G driver-side hub). Sibling apex of the iter-274 topical graph.
const EmployerRequirementsInsuranceHub = lazy(() => import("./pages/EmployerRequirementsInsuranceHub"));
// Iter-274.J — Cluster C pillar / Tier-1 hub (commercial / business-auto
// boundary hub). Third apex in the trilogy — closes the CLUSTERS.C.pillar
// deep-link contract from every cluster-C article body + the
// InterClusterRail cluster-C card.
const CommercialAutoCoverageHub = lazy(() => import("./pages/CommercialAutoCoverageHub"));
// Iter-274.L — Cluster E pillar / Tier-1 hub (costs, claims & decisions).
// Fourth apex hub — answers the MONEY questions (premium delta, post-claim
// surcharge, deductible responsibility). Closes the CLUSTERS.E.pillar
// deep-link contract from every cluster-E article body + the
// InterClusterRail cluster-E card.
const CostAndClaimsDecisionsHub = lazy(() => import("./pages/CostAndClaimsDecisionsHub"));
const InsuranceIndex = lazy(() => import("./pages/InsuranceIndex"));
const InsuranceStatePage = lazy(() => import("./pages/InsuranceStatePage"));
const About = lazy(() => import("./pages/About"));
const Contact = lazy(() => import("./pages/Contact"));
const Privacy = lazy(() => import("./pages/Privacy"));
const DoNotSell = lazy(() => import("./pages/DoNotSell"));
const StateUsuryCapsPage = lazy(() => import("./pages/StateUsuryCapsPage"));
const CoverageTypesHubPage = lazy(() => import("./pages/CoverageTypesHubPage"));
const Terms = lazy(() => import("./pages/Terms"));
const Disclosure = lazy(() => import("./pages/Disclosure"));
const Accessibility = lazy(() => import("./pages/Accessibility"));
const BrandKit = lazy(() => import("./pages/BrandKit"));
const Methodology = lazy(() => import("./pages/Methodology"));
const MethodologySavings = lazy(() => import("./pages/MethodologySavings"));
const LocationsListPage = lazy(() => import("./pages/LocationsListPage"));
const StatePillarHub = lazy(() => import("./pages/StatePillarHub"));
const Glossary = lazy(() => import("./pages/Glossary"));
const GlossaryTerm = lazy(() => import("./pages/GlossaryTerm"));
// Iter-278.K — Data studies are authority-building / linkable assets,
// not editorial guides. Lives under /data/* so the sitemap + Topic Hub
// graph can treat them as a distinct content type.
const CarsLosingValueFastest2026 = lazy(() =>
  import("./pages/data/CarsLosingValueFastest2026"),
);
const EvVsIceOwnershipCostByState2026 = lazy(() =>
  import("./pages/data/EvVsIceOwnershipCostByState2026"),
);
const FirstTimeBuyerOutcomes2025 = lazy(() =>
  import("./pages/data/FirstTimeBuyerOutcomes2025"),
);
const DataStudiesIndex = lazy(() => import("./pages/data/DataStudiesIndex"));
const CityPage = lazy(() => import("./pages/CityPage"));
// Iter-275.Z — HowWeMakeMoney page removed. `/how-we-make-money` route
// is intentionally aliased to the canonical `Disclosure` component (see
// the route registration below + iter-196.II pin) so email recipients
// and on-site clickers reach identical FTC 16 CFR Part 255 content. The
// dedicated HowWeMakeMoney lazy import was unreachable dead code.
const EditorialStandards = lazy(() => import("./pages/EditorialStandards"));
const Corrections = lazy(() => import("./pages/Corrections"));
const EditorsIndex = lazy(() => import("./pages/EditorsIndex"));
const EditorProfile = lazy(() => import("./pages/EditorProfile"));
const ComparisonPage = lazy(() => import("./pages/ComparisonPage"));
const AuthCallback = lazy(() => import("./pages/AuthCallback"));
const AdminLogin = lazy(() => import("./pages/AdminLogin"));
const Admin = lazy(() => import("./pages/Admin"));
const CompareIndex = lazy(() => import("./pages/CompareIndex"));
const BestAutoLoans = lazy(() => import("./pages/BestAutoLoans"));
const AutoLoansIndex = lazy(() => import("./pages/AutoLoansIndex"));
const AutoLoanStatePage = lazy(() => import("./pages/AutoLoanStatePage"));
const AutoLoanCityPage = lazy(() => import("./pages/AutoLoanCityPage"));
const RefinanceHub = lazy(() => import("./pages/RefinanceHub"));
const RefinanceStatePage = lazy(() => import("./pages/RefinanceStatePage"));
const RefinanceCityPage = lazy(() => import("./pages/RefinanceCityPage"));
const NewsletterLanding = lazy(() => import("./pages/NewsletterLanding"));
const NewsletterConfirmed = lazy(() => import("./pages/NewsletterConfirmed"));
const PillarAutoLoans = lazy(() => import("./pages/PillarAutoLoans"));
const PillarAutoInsurance = lazy(() => import("./pages/PillarAutoInsurance"));
const PillarAutoRefinance = lazy(() => import("./pages/PillarAutoRefinance"));
const PillarWarranty = lazy(() => import("./pages/PillarWarranty"));
const CreditTierPlaybook = lazy(() => import("./pages/CreditTierPlaybook"));
const DriverProfilePage = lazy(() => import("./pages/DriverProfilePage"));
const RefiScenarioPage = lazy(() => import("./pages/RefiScenarioPage"));
const EditorialTeam = lazy(() => import("./pages/EditorialTeam"));
const AffiliateRedirect = lazy(() => import("./pages/AffiliateRedirect"));
const MethodologyAutoLoans = lazy(() => import("./pages/MethodologyAutoLoans"));
const MethodologyAutoInsurance = lazy(() => import("./pages/MethodologyAutoInsurance"));
const MethodologyRefinance = lazy(() => import("./pages/MethodologyRefinance"));
const MethodologyWarranty = lazy(() => import("./pages/MethodologyWarranty"));
const WarrantyStatePage = lazy(() => import("./pages/WarrantyStatePage"));
const WarrantyCityPage = lazy(() => import("./pages/WarrantyCityPage"));
const ProviderReviewPage = lazy(() => import("./pages/ProviderReviewPage"));
const ProviderReviewsIndex = lazy(() => import("./pages/ProviderReviewsIndex"));
// Iter-194.B — 6 P1 programmatic SEO hubs (5 single-page guides +
// 1 calculator-by-state hub). Each targets a head-term gap surfaced
// by the audit roll-up. All must be declared BEFORE the wildcard
// `:state` routes in their respective verticals so React Router's
// static-segment ranker picks the literal slug over the param.
const NonOwnerCarInsurance = lazy(() => import("./pages/NonOwnerCarInsurance"));
// Iter-241 — Tier 1 Hub #1: Gap Insurance. Single authoritative hub
// at /gap-insurance + one supporting page at /gap-insurance/refund-after-payoff.
// Both top-level routes — no /:state collision risk.
const GapInsuranceHub = lazy(() => import("./pages/GapInsuranceHub"));
const GapInsuranceRefund = lazy(() => import("./pages/GapInsuranceRefund"));
// Iter-242 — Tier 1 Hub #2: Rideshare Insurance. Hub at
// /insurance/rideshare + 51-state spokes at /insurance/rideshare/:state.
// Both literal segments win React Router's static-segment ranker
// against /insurance/:state and /insurance/:state/:city.
const RideshareInsuranceHub = lazy(() => import("./pages/RideshareInsuranceHub"));
const RideshareInsuranceStatePage = lazy(() => import("./pages/RideshareInsuranceStatePage"));
// Iter-244 — Tier 1 Hub #3: Auto Loan Denial Recovery. Hub +
// supporting pre-approval checklist page. Both registered as
// literal /guides/... routes BEFORE the wildcard /guides/:slug.
const AutoLoanDeniedWhatNext = lazy(() => import("./pages/AutoLoanDeniedWhatNext"));
const AutoLoanPreApprovalChecklist = lazy(() => import("./pages/AutoLoanPreApprovalChecklist"));
// Iter-245 — Tier 1 Hub #4: Cost of Ownership. Hub at /cost-of-ownership
// + supporting guide at /guides/total-cost-of-ownership-by-vehicle-class.
const CostOfOwnershipHub = lazy(() => import("./pages/CostOfOwnershipHub"));
const TcoByVehicleClass = lazy(() => import("./pages/TcoByVehicleClass"));
// Iter-246–249 — Tier 1 Hubs #5–#8: Co-signer, Diminished Value,
// Repossession Survival, Moving States. All literal /guides/ routes
// registered BEFORE /guides/:slug fall-through.
const CosignerHub = lazy(() => import("./pages/CosignerHub"));
const DiminishedValueHub = lazy(() => import("./pages/DiminishedValueHub"));
const RepossessionSurvivalHub = lazy(() => import("./pages/RepossessionSurvivalHub"));
const MovingStatesHub = lazy(() => import("./pages/MovingStatesHub"));
// Iter-250–252 — Tier 2 Hubs: First-Accident Impact, Telematics /
// Usage-Based Insurance, and 0% APR vs Cash Rebate. All three mount
// proprietary embedded calculators and register as literal /guides/
// routes BEFORE /guides/:slug fall-through.
const FirstAccidentImpactHub = lazy(() => import("./pages/FirstAccidentImpactHub"));
const TelematicsInsuranceHub = lazy(() => import("./pages/TelematicsInsuranceHub"));
const ZeroPercentVsRebateHub = lazy(() => import("./pages/ZeroPercentVsRebateHub"));
const MilitaryAutoLoans = lazy(() => import("./pages/MilitaryAutoLoans"));
const SelfEmployedAutoLoans = lazy(() => import("./pages/SelfEmployedAutoLoans"));
const RefinanceCalculatorByState = lazy(() => import("./pages/RefinanceCalculatorByState"));
const WarrantyHighMileage = lazy(() => import("./pages/WarrantyHighMileage"));
const WarrantyVsMBI = lazy(() => import("./pages/WarrantyVsMBI"));
const WarrantyModelHub = lazy(() => import("./pages/WarrantyModelHub"));
const WarrantyModelPage = lazy(() => import("./pages/WarrantyModelPage"));
const Unsubscribe = lazy(() => import("./pages/Unsubscribe"));
const EmailPreferences = lazy(() => import("./pages/EmailPreferences"));
const CheapestStateVehiclePage = lazy(() => import("./pages/CheapestStateVehiclePage"));
const NewCarLoans = lazy(() => import("./pages/NewCarLoans"));
const UsedCarLoans = lazy(() => import("./pages/UsedCarLoans"));
const FirstTimeBuyerAutoLoans = lazy(() => import("./pages/FirstTimeBuyerAutoLoans"));
const CalculatorsIndex = lazy(() => import("./pages/CalculatorsIndex"));
const BestOfIndex = lazy(() => import("./pages/BestOfIndex"));
const RoundupPageRoute = lazy(() => import("./pages/RoundupPage"));
const ProviderVsProviderPage = lazy(() => import("./pages/ProviderVsProviderPage"));
// Iter-185.BN (AUDIT #8) — Programmatic Carrier × State landing page.
// /auto-insurance/<state-slug>/<carrier-slug> — 14 carriers × 51 states = 714 routes.
const CarrierStatePage = lazy(() => import("./pages/CarrierStatePage"));
// Iter-185.BR (AUDIT #8 Phase 3) — Carrier × State × Vehicle landing
// page. /auto-insurance/<state>/<carrier>/<vehicle> — 14 × 51 × 3 = 2,142
// long-tail SEO routes layered ON TOP of the parent carrier × state matrix.
const CarrierStateVehiclePage = lazy(() => import("./pages/CarrierStateVehiclePage"));
// Iter-185.BS (AUDIT #8 Phase 4) — Carrier × State × Driver-Persona
// landing page. /auto-insurance/<state>/<carrier>/for/<persona> —
// 14 × 51 × 4 = 2,856 new long-tail SEO routes. `/for/` discriminator
// avoids collision with the iter-185.BR vehicle pattern. Includes
// truthful "ineligibility variant" pages for the 7 carriers that
// don't write SR-22.
const CarrierStatePersonaPage = lazy(() => import("./pages/CarrierStatePersonaPage"));
const RateAlertsPage = lazy(() => import("./pages/RateAlertsPage"));
const Warranty = lazy(() => import("./pages/Warranty"));
const NotFound = lazy(() => import("./pages/NotFound"));
const CityOrPersonaPage = lazy(() => import("./pages/CityOrPersonaPage"));
// PersonaPage + CityPage are eagerly imported INSIDE CityOrPersonaPage via
// its own static imports, so they ride the same chunk without explicit
// lazy() wrappers here.
// Iter-193.C — Persona-FIRST URL hierarchy. National hub at
// /insurance/<persona> + per-state spokes at /insurance/<persona>/<state>.
// Builds the NerdWallet/Bankrate-style topical cluster.
const PersonaHub = lazy(() => import("./pages/PersonaHub"));
const PersonaPageLazy = lazy(() => import("./pages/PersonaPage"));
// Iter-193.D — No-credit-check auto loans single-page guide (47k mo. vol).
const NoCreditCheckAutoLoans = lazy(() => import("./pages/NoCreditCheckAutoLoans"));
// Iter-227 — Internal /design-system route. Internal-only reference,
// noindex, not in the sitemap, not linked from public nav.
const DesignSystem = lazy(() => import("./pages/DesignSystem"));

/** Branded loading fallback shown while a code-split chunk is being fetched.
 *
 * Iter-275.W (CWV fix) — Uses `min-h-screen` so the fallback reserves
 * the FULL viewport height. Before, the `min-h-[60vh]` reservation
 * (~648 px on a 1080 px desktop) was much shorter than the actual
 * page that mounted (~9000 px on /auto-insurance), so when the lazy
 * chunk arrived and React swapped the fallback out, the document
 * height changed by thousands of pixels and the browser logged a
 * CLS spike (production measured 0.25 CLS on every non-homepage
 * route, well above Google's 0.1 "Good" threshold). Reserving the
 * full viewport during the swap moves the shifted region OFF-screen
 * so it doesn't count toward CLS. */
function RouteSuspenseFallback() {
  return (
    <div
      data-testid="route-suspense-fallback"
      className="min-h-screen flex items-start justify-center bg-white pt-32"
      role="status"
      aria-live="polite"
    >
      <div className="flex items-center gap-3 text-slate-500 text-sm font-medium">
        <div className="h-4 w-4 rounded-full border-2 border-slate-300 border-t-emerald-500 animate-spin" aria-hidden="true" />
        <span>Loading…</span>
      </div>
    </div>
  );
}

/** Legacy /articles → /guides 301-equivalent redirect with SEO crawler hints.
 *
 * SPA-level <Navigate> only works for JS-enabled browsers. To approximate a
 * server 301 for JS-disabled crawlers (Googlebot does execute JS, but Bingbot
 * + others have weaker JS support), we ALSO emit:
 *   <meta http-equiv="refresh" content="0; url=/guides...">  — non-JS fallback
 *   <link rel="canonical" href="/guides...">                  — concentrates link equity
 *   <meta name="robots" content="noindex">                   — prevents legacy URL indexing
 */
function GuidesRedirect({ slug }) {
  const target = slug ? `/guides/${slug}` : "/guides";
  return (
    <>
      <Helmet>
        <meta httpEquiv="refresh" content={`0; url=${target}`} />
        <link rel="canonical" href={target} />
        <meta name="robots" content="noindex, follow" />
      </Helmet>
      <Navigate to={target} replace />
    </>
  );
}

/** Legacy /articles/:slug → /guides/:slug 301-equivalent redirect. */
function ArticlesSlugRedirect() {
  const { slug } = useParams();
  return <GuidesRedirect slug={slug} />;
}

/** Iter-253 — Deep-link /admin/articles/:slug → unified admin shell
 *  with the articles tab + the requested article auto-opened in the
 *  editor (ArticlesPanel reads `?edit=<slug>`). Lets editorial bookmark
 *  or paste a direct URL to any of the 274 Claude-generated TL;DRs. */
function AdminArticleSlugRedirect() {
  const { slug } = useParams();
  return <Navigate to={`/admin?tab=articles&edit=${encodeURIComponent(slug)}`} replace />;
}

function AppRouter() {
  const location = useLocation();
  const isAdminArea = location.pathname.startsWith("/admin");

  // Synchronously detect session_id in URL fragment and route to AuthCallback
  // BEFORE any other route renders. Prevents AuthProvider race conditions.
  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }

  return (
    <QuoteModalProvider ModalComponent={QuoteModal}>
      <CompareStashProvider>
      {/* Iter-275.AR (Phase 1 #15 of Production Readiness Audit) —
          Belt-and-braces SEO defense for admin routes. robots.txt
          already disallows /admin* but the audit flagged that a
          curious bot ignoring robots.txt can still index the admin
          login HTML shell. Helmet emits a route-level
          `<meta name="robots" content="noindex,nofollow,noarchive">`
          on every /admin/* request — Googlebot honours this even when
          robots.txt is missed, and rich-results crawlers (Bing, Yandex,
          DuckDuckBot) treat it as canonical. Scoped to the SPA shell
          only; backend admin API surface is JWT-gated separately. */}
      {isAdminArea && (
        <Helmet>
          <meta name="robots" content="noindex, nofollow, noarchive" />
          <meta name="googlebot" content="noindex, nofollow, noarchive" />
          <meta name="bingbot" content="noindex, nofollow, noarchive" />
        </Helmet>
      )}
      {/* Skip-to-content link — visible only when keyboard-focused */}
      {!isAdminArea && (
        <a
          href="#main-content"
          data-testid="skip-to-content-link"
          className="sr-only focus:not-sr-only focus:fixed focus:top-3 focus:left-3 focus:z-[100] focus:bg-[#0A2540] focus:text-white focus:px-4 focus:py-2 focus:font-bold focus:text-sm focus:outline-none focus:ring-2 focus:ring-[#10B981]"
        >
          Skip to content
        </a>
      )}
      {!isAdminArea && <Header />}
      {!isAdminArea && <ReadingProgress />}
      <div id="main-content">
      <ErrorBoundary>
      <Suspense fallback={<RouteSuspenseFallback />}>
      <Routes>
        <Route path="/" element={<Home />} />
        <Route path="/guides" element={<ArticlesIndex />} />
        <Route path="/guides/auto-loans-complete-guide" element={<PillarAutoLoans />} />
        <Route path="/guides/auto-insurance-complete-guide" element={<PillarAutoInsurance />} />
        <Route path="/guides/auto-refinance-complete-guide" element={<PillarAutoRefinance />} />
        <Route path="/guides/extended-warranty-complete-guide" element={<PillarWarranty />} />
        {/* Iter-244 — Tier 1 Hub #3: Auto Loan Denial Recovery.
            Two literal /guides/ routes registered BEFORE the
            generic /guides/:slug fall-through later in this file. */}
        <Route path="/guides/auto-loan-denied-what-next" element={<AutoLoanDeniedWhatNext />} />
        <Route path="/guides/auto-loan-pre-approval-checklist" element={<AutoLoanPreApprovalChecklist />} />
        {/* Iter-245 — Tier 1 Hub #4: Cost of Ownership. Top-level hub +
            supporting /guides/ literal both registered BEFORE
            /guides/:slug fall-through. */}
        <Route path="/cost-of-ownership" element={<CostOfOwnershipHub />} />
        <Route path="/guides/total-cost-of-ownership-by-vehicle-class" element={<TcoByVehicleClass />} />
        {/* Iter-246–249 — Tier 1 Hubs #5–#8 literal /guides/ routes */}
        <Route path="/guides/auto-loan-co-signer" element={<CosignerHub />} />
        <Route path="/guides/diminished-value-claim" element={<DiminishedValueHub />} />
        <Route path="/guides/repossession-survival" element={<RepossessionSurvivalHub />} />
        <Route path="/guides/switching-car-insurance-when-moving" element={<MovingStatesHub />} />
        {/* Iter-250–252 — Tier 2 Hubs: First-Accident Impact, Telematics,
            0% APR vs Cash Rebate. Literal /guides/ routes registered
            BEFORE /guides/:slug fall-through. */}
        <Route path="/guides/first-accident-insurance-impact" element={<FirstAccidentImpactHub />} />
        <Route path="/guides/telematics-insurance" element={<TelematicsInsuranceHub />} />
        <Route path="/guides/zero-apr-vs-cash-rebate" element={<ZeroPercentVsRebateHub />} />
        {/* Iter-196.EE — 4 credit-tier auto-loan playbooks. Single
            CreditTierPlaybook component resolves the tier from the
            propTier prop (so each route has a fixed slug + the
            playbook page can pre-resolve its tier without URL
            ambiguity). */}
        <Route path="/guides/subprime-auto-loan-playbook" element={<CreditTierPlaybook tier="subprime" />} />
        <Route path="/guides/near-prime-auto-loan-playbook" element={<CreditTierPlaybook tier="near-prime" />} />
        <Route path="/guides/prime-auto-loan-playbook" element={<CreditTierPlaybook tier="prime" />} />
        <Route path="/guides/super-prime-auto-loan-playbook" element={<CreditTierPlaybook tier="super-prime" />} />
        {/* Iter-196.GG — 8 driver-profile / SR-22 insurance pages.
            Single DriverProfilePage component resolves the profile from
            the propProfile prop. Targets the highest-CPC affiliate
            queries in auto insurance (SR-22 ~22k/mo $35-50 CPC, DUI
            ~14k/mo, teen-driver ~18k/mo, high-risk ~9.4k/mo, etc.). */}
        <Route path="/guides/sr-22-insurance" element={<DriverProfilePage profile="sr-22" />} />
        <Route path="/guides/fr-44-insurance" element={<DriverProfilePage profile="fr-44" />} />
        <Route path="/guides/dui-insurance" element={<DriverProfilePage profile="dui" />} />
        <Route path="/guides/lapsed-coverage-insurance" element={<DriverProfilePage profile="lapsed-coverage" />} />
        <Route path="/guides/multi-accident-driver-insurance" element={<DriverProfilePage profile="multi-accident" />} />
        <Route path="/guides/foreign-license-insurance" element={<DriverProfilePage profile="foreign-license" />} />
        <Route path="/guides/teen-driver-insurance" element={<DriverProfilePage profile="teen-driver" />} />
        <Route path="/guides/high-risk-driver-insurance" element={<DriverProfilePage profile="high-risk" />} />
        {/* Iter-196.HH — 10 refi scenario playbooks. Single
            RefiScenarioPage component resolves the scenario from the
            propScenario prop. Targets the 10 highest-search-volume
            refinance scenarios (lease buyout ~9.9k/mo, rebuilt-credit
            ~6.6k/mo, cash-out ~5.4k/mo, etc.). */}
        <Route path="/guides/lease-buyout-refinance" element={<RefiScenarioPage scenario="lease-buyout" />} />
        <Route path="/guides/rebuilt-credit-refinance" element={<RefiScenarioPage scenario="rebuilt-credit" />} />
        <Route path="/guides/cash-out-auto-refinance" element={<RefiScenarioPage scenario="cash-out" />} />
        <Route path="/guides/private-party-purchase-refinance" element={<RefiScenarioPage scenario="private-party" />} />
        <Route path="/guides/dealer-financing-escape-refinance" element={<RefiScenarioPage scenario="dealer-financing-escape" />} />
        <Route path="/guides/negative-equity-refinance" element={<RefiScenarioPage scenario="negative-equity" />} />
        <Route path="/guides/post-bankruptcy-refinance" element={<RefiScenarioPage scenario="post-bankruptcy" />} />
        <Route path="/guides/military-pcs-refinance" element={<RefiScenarioPage scenario="military-pcs" />} />
        <Route path="/guides/divorce-auto-refinance" element={<RefiScenarioPage scenario="divorce" />} />
        <Route path="/guides/high-mileage-refinance" element={<RefiScenarioPage scenario="high-mileage" /> } />
        <Route path="/about/editorial-team" element={<EditorialTeam />} />
        <Route path="/redirect/:category" element={<AffiliateRedirect />} />
        <Route path="/methodology/auto-loans" element={<MethodologyAutoLoans />} />
        <Route path="/methodology/auto-insurance" element={<MethodologyAutoInsurance />} />
        <Route path="/methodology/refinance" element={<MethodologyRefinance />} />
        <Route path="/methodology/warranty" element={<MethodologyWarranty />} />
        <Route path="/methodology/savings" element={<MethodologySavings />} />
        <Route path="/guides/:slug" element={<ArticlePage />} />
        {/* Legacy /articles → /guides 301-equivalent client redirects.
            Preserves SEO equity for any inbound links pointing to the old URL. */}
        <Route path="/articles" element={<GuidesRedirect />} />
        <Route
          path="/articles/:slug"
          element={<ArticlesSlugRedirect />}
        />
        <Route path="/auto-loan-calculator" element={<AutoLoanCalculator />} />
        <Route path="/auto-loan-refinance-calculator" element={<RefinanceCalculator />} />
        {/* Legacy URL — kept as a 301-equivalent client redirect so old
            inbound links / external citations keep working. */}
        <Route
          path="/refinance-calculator"
          element={<Navigate to="/auto-loan-refinance-calculator" replace />}
        />
        <Route path="/car-affordability-calculator" element={<AffordabilityCalculator />} />
        <Route path="/auto-insurance-cost-estimator" element={<InsuranceEstimator />} />
        <Route path="/auto-insurance-increase-calculator" element={<InsuranceIncreaseCalculator />} />
        {/* Iter-175 — Common-typo slug 301-equivalent so users who land on
            the verbose URL still reach the calculator. */}
        <Route
          path="/auto-insurance-rate-increase-calculator"
          element={<Navigate to="/auto-insurance-increase-calculator" replace />}
        />
        <Route
          path="/car-insurance-increase-calculator"
          element={<Navigate to="/auto-insurance-increase-calculator" replace />}
        />
        <Route path="/trade-in-value-estimator" element={<TradeInValueEstimator />} />
        {/* Iter-196.T — 5 new calculators per audit. */}
        <Route path="/lease-vs-buy-calculator" element={<LeaseVsBuyCalculator />} />
        <Route path="/bi-weekly-payment-calculator" element={<BiWeeklyPaymentCalculator />} />
        <Route path="/title-tax-fee-calculator" element={<TitleTaxFeeCalculator />} />
        <Route path="/total-interest-calculator" element={<TotalInterestCalculator />} />
        <Route path="/insurance-premium-predictor" element={<InsurancePremiumPredictor />} />
        <Route path="/extended-warranty-worth-it-calculator" element={<ExtendedWarrantyWorthIt />} />
        {/* Iter-418 — /calculators/{tool} namespace landing. */}
        <Route path="/calculators/deductible-break-even" element={<DeductibleBreakEvenPage />} />
        <Route
          path="/deductible-calculator"
          element={<Navigate to="/calculators/deductible-break-even" replace />}
        />
        {/* Iter-275 — Multi-vertical Savings Opportunity Score (P0 competitive
            differentiator: synthesises insurance + refi + warranty savings
            into one 2-minute flow; no competitor ships a multi-vertical
            version of this). Canonical URL — also aliased under /tools/ to
            match the URL pattern emerging on calculators-index. */}
        <Route path="/savings-opportunity-score" element={<SavingsOpportunityScore />} />
        <Route path="/tools/savings-opportunity-score" element={<Navigate to="/savings-opportunity-score" replace />} />
        {/* Iter-274.G — Cluster A pillar / Tier-1 hub. Apex of the iter-274
            topical-authority graph (closes deep-links from state-page rail
            + InterClusterRail). */}
        <Route path="/auto-insurance/business-use-coverage" element={<BusinessUseInsuranceHub />} />
        {/* Iter-274.H — Cluster B pillar / Tier-1 hub. Employer-side companion
            to the 274.G driver-side hub. Closes deep-links from the
            InterClusterRail cluster B card. */}
        <Route path="/auto-insurance/employer-requirements" element={<EmployerRequirementsInsuranceHub />} />
        {/* Iter-274.J — Cluster C pillar / Tier-1 hub. Third apex in the
            iter-274 trilogy — the commercial/business-auto boundary hub.
            Closes the CLUSTERS.C.pillar deep-link contract from every
            cluster-C article body + the InterClusterRail cluster-C card. */}
        <Route path="/auto-insurance/commercial-auto-coverage" element={<CommercialAutoCoverageHub />} />
        {/* Iter-274.L — Cluster E pillar / Tier-1 hub. Fourth apex hub —
            the costs/claims/decisions money-questions hub. Closes the
            CLUSTERS.E.pillar deep-link contract from every cluster-E
            article body + the InterClusterRail cluster-E card. */}
        <Route path="/auto-insurance/cost-and-claims-decisions" element={<CostAndClaimsDecisionsHub />} />
        <Route path="/total-cost-of-ownership-calculator" element={<TcoCalculator />} />
        {/* Iter-276.F (Top-20 #19) — Canonical /tco-by-vehicle-class
            landing page. Surfaces the VehicleClassTcoMatrix component
            (previously buried inside /cost-of-ownership) on its own
            SEO-targeted URL. Targets ~15 long-tail TCO queries that
            currently flow to Edmunds / KBB. */}
        <Route path="/tco-by-vehicle-class" element={<VehicleClassTcoPage />} />
        {/* Iter-414 — Standalone landing for the Cybercab profit calculator
            embed (also mounted inside the /guides/tesla-cybercab-cost-ownership
            guide). */}
        <Route path="/cybercab-profit-calculator" element={<CybercabCalculatorPage />} />
        <Route path="/true-cost-to-own" element={<Navigate to="/total-cost-of-ownership-calculator" replace />} />
        <Route path="/tco-calculator" element={<Navigate to="/total-cost-of-ownership-calculator" replace />} />
        {/* Iter-175 — Spec URL alias for the SEO long-tail "total car
            ownership cost calculator" keyword cluster. Canonical URL
            remains /total-cost-of-ownership-calculator. */}
        <Route
          path="/total-car-ownership-cost-calculator"
          element={<Navigate to="/total-cost-of-ownership-calculator" replace />}
        />
        {/* Legacy slug 301-equivalent redirect — preserves SEO equity for any
            inbound links pointing to the old URL. */}
        <Route
          path="/insurance-cost-estimator"
          element={<Navigate to="/auto-insurance-cost-estimator" replace />}
        />
        <Route path="/auto-insurance" element={<InsuranceIndex />} />
        {/* "View all" location index pages — one component, four variant keys */}
        <Route path="/auto-insurance/states" element={<StatePillarHub variant="insurance" />} />
        <Route path="/auto-insurance/cities" element={<LocationsListPage variantKey="auto-insurance-cities" />} />
        {/* Iter-185.BN (AUDIT #8) — Programmatic Carrier × State landing
            pages: 14 carriers × 51 states = 714 long-tail SEO routes.
            URL pattern: /auto-insurance/<state-slug>/<carrier-slug>.
            Specific routes (/states, /cities) are listed BEFORE the
            programmatic pattern so React Router resolves them first.
            Iter-185.BR (AUDIT #8 Phase 3) — The 3-segment variant
            /auto-insurance/<state>/<carrier>/<vehicle> is registered
            FIRST so React Router prefers the more-specific match.
            Iter-185.BS (AUDIT #8 Phase 4) — Driver-persona variant
            uses a /for/ discriminator so it doesn't collide with the
            vehicle-class 3-segment route. */}
        <Route
          path="/auto-insurance/:stateSlug/:carrierSlug/for/:personaSlug"
          element={<CarrierStatePersonaPage />}
        />
        <Route
          path="/auto-insurance/:stateSlug/:carrierSlug/:vehicleSlug"
          element={<CarrierStateVehiclePage />}
        />
        <Route
          path="/auto-insurance/:stateSlug/:carrierSlug"
          element={<CarrierStatePage />}
        />
        <Route path="/auto-loans/states" element={<StatePillarHub variant="auto-loan" />} />
        <Route path="/auto-loans/cities" element={<LocationsListPage variantKey="auto-loans-cities" />} />
        {/* Legacy URL → 301-equivalent client redirect. State + city sub-paths
            keep their `/insurance/...` prefix below for SEO continuity. */}
        <Route path="/insurance" element={<Navigate to="/auto-insurance" replace />} />
        {/* Iter-193.C/D — Persona-FIRST URL hierarchy. Must come BEFORE
            `/insurance/:state` so React Router's static-segment ranker
            picks the persona route over the state slug param. Hub +
            spokes build the cluster authority that wins persona head
            terms (e.g. "teen driver car insurance"). */}
        <Route path="/insurance/teen-drivers" element={<PersonaHub personaSlug="teen-drivers" />} />
        <Route
          path="/insurance/teen-drivers/:state"
          element={<PersonaPageLazy personaOverride="teen-drivers" urlVariant="persona-first" />}
        />
        <Route path="/insurance/sr-22" element={<PersonaHub personaSlug="sr-22" />} />
        <Route
          path="/insurance/sr-22/:state"
          element={<PersonaPageLazy personaOverride="sr-22" urlVariant="persona-first" />}
        />
        <Route path="/insurance/after-dui" element={<PersonaHub personaSlug="after-dui" />} />
        <Route
          path="/insurance/after-dui/:state"
          element={<PersonaPageLazy personaOverride="after-dui" urlVariant="persona-first" />}
        />
        <Route path="/insurance/:state" element={<InsuranceStatePage />} />
        <Route path="/warranty" element={<Warranty />} />
        <Route path="/about" element={<About />} />
        <Route path="/contact" element={<Contact />} />
        <Route path="/privacy" element={<Privacy />} />
        <Route path="/do-not-sell" element={<DoNotSell />} />
        <Route path="/state-usury-caps" element={<StateUsuryCapsPage />} />
        <Route path="/coverage-types" element={<CoverageTypesHubPage />} />
        <Route path="/terms" element={<Terms />} />
        <Route path="/disclosure" element={<Disclosure />} />
        {/* Iter-196.II Sprint 1 (Email H-1) — `/how-we-make-money` alias.
            Drip + lifecycle + broadcast email footers all link to
            `/how-we-make-money` (the user-friendly URL). This alias
            renders the same Disclosure component so email recipients
            land on identical content as on-site /disclosure clickers. */}
        <Route path="/how-we-make-money" element={<Disclosure />} />
        <Route path="/accessibility" element={<Accessibility />} />
        <Route path="/brand" element={<BrandKit />} />
        <Route path="/methodology" element={<Methodology />} />
        <Route path="/glossary" element={<Glossary />} />
        <Route path="/glossary/:term" element={<GlossaryTerm />} />
        {/* Iter-278.K/L — Data study namespace. Linkable assets for
            authority + PR. /data hub indexes every shipped study. */}
        <Route path="/data" element={<DataStudiesIndex />} />
        <Route
          path="/data/cars-losing-value-fastest-2026"
          element={<CarsLosingValueFastest2026 />}
        />
        <Route
          path="/data/ev-vs-ice-ownership-cost-by-state-2026"
          element={<EvVsIceOwnershipCostByState2026 />}
        />
        <Route
          path="/data/first-time-buyer-outcomes-2025"
          element={<FirstTimeBuyerOutcomes2025 />}
        />
        {/* Iter-194.B — P1 single-page guide. Must come BEFORE
            /insurance/:state/:city so the literal slug wins routing. */}
        <Route path="/insurance/non-owner-car-insurance" element={<NonOwnerCarInsurance />} />
        {/* Iter-242 — Tier 1 Hub #2: Rideshare Insurance.
            Hub + 51-state spokes. Literal segments registered BEFORE
            the wildcard /insurance/:state route so React Router picks
            them. */}
        <Route path="/insurance/rideshare" element={<RideshareInsuranceHub />} />
        <Route path="/insurance/rideshare/:state" element={<RideshareInsuranceStatePage />} />
        {/* Iter-241 — Tier 1 Hub #1: Gap Insurance. Top-level routes,
            no collision with any /:state wildcard. Sub-page comes
            BEFORE the hub so React Router's longest-static-match
            wins routing. */}
        <Route path="/gap-insurance/refund-after-payoff" element={<GapInsuranceRefund />} />
        <Route path="/gap-insurance" element={<GapInsuranceHub />} />
        <Route path="/insurance/:state/:city" element={<CityOrPersonaPage />} />
        {/* Long-form SEO guides */}
        <Route path="/best-auto-loan-lenders" element={<BestAutoLoans />} />
        <Route path="/lender-reviews" element={<ProviderReviewsIndex />} />
        <Route path="/lender-reviews/:slug" element={<ProviderReviewPage />} />
        <Route path="/auto-loans" element={<AutoLoansIndex />} />
        {/* Iter-193.D — Single-page guide. Must come BEFORE
            `/auto-loans/:state` so React Router's static-segment
            ranker picks this route over the state slug param. */}
        <Route path="/auto-loans/no-credit-check" element={<NoCreditCheckAutoLoans />} />
        {/* Iter-194.B — P1 single-page guides. Must come BEFORE
            /auto-loans/:state so the literal slugs win routing. */}
        <Route path="/auto-loans/military" element={<MilitaryAutoLoans />} />
        <Route path="/auto-loans/self-employed" element={<SelfEmployedAutoLoans />} />
        <Route path="/auto-loans/:state" element={<AutoLoanStatePage />} />
        {/* Iter-81 — per-city auto-loan landing pages. Sibling pattern
            to the /insurance/:state/:city routes shipped earlier. */}
        <Route path="/auto-loans/:state/:city" element={<AutoLoanCityPage />} />
        <Route path="/refinance" element={<RefinanceHub />} />
        {/* Iter-194.B — P1 calculator-by-state hub. Must come BEFORE
            /refinance/:state so the literal slug wins routing. */}
        <Route path="/refinance/calculator-by-state" element={<RefinanceCalculatorByState />} />
        <Route path="/refinance/:state" element={<RefinanceStatePage />} />
        <Route path="/refinance/:state/:city" element={<RefinanceCityPage />} />
        <Route path="/newsletter" element={<NewsletterLanding />} />
        <Route path="/newsletter/confirmed" element={<NewsletterConfirmed />} />
        {/* Iter-194.B — P1 single-page warranty guides. Must come BEFORE
            /warranty/:state so the literal slugs win routing. */}
        <Route path="/warranty/used-cars-over-100k-miles" element={<WarrantyHighMileage />} />
        <Route path="/warranty/vs-mechanical-breakdown-insurance" element={<WarrantyVsMBI />} />
        {/* Iter-223 — Top-50 model warranty pages. Literal /warranty/models
            and /warranty/model/:slug must come BEFORE /warranty/:state so
            the literal segments win routing. */}
        <Route path="/warranty/models" element={<WarrantyModelHub />} />
        <Route path="/warranty/model/:slug" element={<WarrantyModelPage />} />
        {/* Iter-142 — Per-state extended-warranty pages (51 states, closes
            the state × category landing matrix gap). */}
        <Route path="/warranty/:state" element={<WarrantyStatePage />} />
        <Route path="/warranty/:state/:city" element={<WarrantyCityPage />} />
        <Route path="/auto-loan-refinance" element={<Navigate to="/refinance" replace />} />
        <Route path="/auto-refinance" element={<Navigate to="/refinance" replace />} />
        <Route path="/new-car-loans" element={<NewCarLoans />} />
        <Route path="/used-car-loans" element={<UsedCarLoans />} />
        <Route path="/first-time-buyer-auto-loans" element={<FirstTimeBuyerAutoLoans />} />
        <Route path="/how-to-lower-car-payment" element={<GuidePage />} />
        <Route path="/lease-vs-buy" element={<GuidePage />} />
        <Route path="/car-loan-rates" element={<GuidePage />} />
        {/* Iter-275.Z — `/advertiser-disclosure` was referenced from
            MethodologySavings.jsx but had no Route, falling through to
            the 404 catch-all. Redirect to the canonical `/disclosure`
            page (same FTC-mandated content). */}
        <Route
          path="/advertiser-disclosure"
          element={<Navigate to="/disclosure" replace />}
        />
        {/* Iter-275.Z — Dead duplicate `/how-we-make-money` Route to the
            standalone page component was removed (the canonical alias on
            line 540 routes the URL to the `Disclosure` component, pinned by
            test_iter196ii). */}
        <Route path="/editorial-standards" element={<EditorialStandards />} />
        <Route path="/corrections" element={<Corrections />} />
        <Route path="/editors" element={<EditorsIndex />} />
        <Route path="/editors/:slug" element={<EditorProfile />} />
        <Route path="/compare" element={<CompareIndex />} />
        <Route path="/calculators" element={<CalculatorsIndex />} />
        {/* "Best of" authority hubs */}
        <Route path="/best" element={<BestOfIndex />} />
        {ROUNDUPS.map((r) => (
          <Route
            key={r.slug}
            path={`/${r.slug}`}
            element={<RoundupPageRoute slug={r.slug} />}
          />
        ))}
        {/* "X vs Y" comparison pages */}
        {COMPARISONS.map((c) => (
          <Route key={c.slug} path={`/${c.slug}`} element={<ComparisonPage />} />
        ))}
        {/* Lender-vs-lender / Carrier-vs-carrier programmatic SEO pages */}
        {/* Iter-185.AX — Category-specific canonical URL prefixes so the
            visible URL matches the page's actual subject:
              /compare-loans/<pair>     for auto-loan pairs
              /compare-refinance/<pair> for refi pairs
              /compare-insurance/<pair> for insurance carrier pairs
            Legacy `/compare-lenders/<pair>` 301-redirects to the
            category-correct URL inside ProviderVsProviderPage so existing
            backlinks are preserved. */}
        <Route path="/compare-lenders" element={<Navigate to="/compare" replace />} />
        <Route path="/compare-lenders/:pairSlug" element={<ProviderVsProviderPage />} />
        <Route path="/compare-loans/:pairSlug" element={<ProviderVsProviderPage />} />
        <Route path="/compare-refinance/:pairSlug" element={<ProviderVsProviderPage />} />
        <Route path="/compare-insurance/:pairSlug" element={<ProviderVsProviderPage />} />
        <Route path="/rate-alerts" element={<RateAlertsPage />} />
        <Route path="/unsubscribe" element={<Unsubscribe />} />
        <Route path="/email-preferences" element={<EmailPreferences />} />
        {/* Iter-227 — Internal /design-system reference. Noindex via
            Helmet inside the page; intentionally NOT added to the
            sitemap and NOT linked from public nav. */}
        <Route path="/design-system" element={<DesignSystem />} />
        <Route
          path="/cheapest-insurance/:state/:vehicle"
          element={<CheapestStateVehiclePage />}
        />
        {/* Admin */}
        <Route path="/admin" element={<Admin />} />
        <Route path="/admin/login" element={<AdminLogin />} />
        {/* Iter-122 — Deep-link aliases so /admin/{tab} resolves to the
            corresponding panel inside the unified Admin shell. */}
        <Route path="/admin/funnel" element={<Navigate to="/admin?tab=funnel" replace />} />
        <Route path="/admin/routing" element={<Navigate to="/admin?tab=routing" replace />} />
        {/* Iter-149 — alias the path-style names referenced in iter-145/148 specs
            (and the docs / external links) to the canonical ?tab= query route.
            Without these aliases /admin/partner-routing + /admin/lender-rates
            fell through to the 404 catch-all. */}
        <Route path="/admin/partner-routing" element={<Navigate to="/admin?tab=routing" replace />} />
        <Route path="/admin/lender-rates" element={<Navigate to="/admin?tab=rates" replace />} />
        <Route path="/admin/rates" element={<Navigate to="/admin?tab=rates" replace />} />
        <Route path="/admin/promotions" element={<Navigate to="/admin?tab=promotions" replace />} />
        <Route path="/admin/seo" element={<Navigate to="/admin?tab=seo" replace />} />
        <Route path="/admin/state-data" element={<Navigate to="/admin?tab=state-data" replace />} />
        <Route path="/admin/performance" element={<Navigate to="/admin?tab=performance" replace />} />
        <Route path="/admin/topical" element={<Navigate to="/admin?tab=topical" replace />} />
        <Route path="/admin/outreach" element={<Navigate to="/admin?tab=outreach" replace />} />
        {/* Iter-253 — Deep-link to a specific article's editor. Resolves
            to /admin?tab=articles&edit=<slug>; ArticlesPanel watches
            the search params and auto-opens the editor on load. */}
        <Route path="/admin/articles" element={<Navigate to="/admin?tab=articles" replace />} />
        <Route path="/admin/articles/:slug" element={<AdminArticleSlugRedirect />} />
        {/* 404 catch-all — MUST be last */}
        <Route path="*" element={<NotFound />} />
      </Routes>
      </Suspense>
      </ErrorBoundary>
      </div>
      {!isAdminArea && (
        <>
          <Footer />
          <StickyCTA />
          <MobileStickyBar />
          <LiveSocialProof />
          <ExitIntentModal />
          <StickyCompareDrawer />
          <CompareDrawerModal />
          <MobileStickyCompareCTA />
          <RedirectGuard />
          <CookieConsentBanner />
        </>
      )}
      </CompareStashProvider>
    </QuoteModalProvider>
  );
}

function App() {
  // Iter-49: Hydrate the MongoDB-backed partner routing registry on app boot,
  // and re-hydrate whenever the `partner_routing` cache resource bumps
  // (admin flipped a category to a live partner — public intake forms pick
  // it up without a hard reload).
  useEffect(() => { hydratePartnerRouting(); }, []);
  useRevalidateOnCacheBump("partner_routing", hydratePartnerRouting);

  // Iter-165 — Hydrate admin-editable funnel copy (success card copy,
  // email-capture copy, CTAs, affiliate URLs). Re-hydrates on
  // cache-version bump so admin edits propagate without a hard reload.
  useEffect(() => { hydrateFunnelCopy(); }, []);
  useRevalidateOnCacheBump("funnel_copy", hydrateFunnelCopy);

  // Iter-127 — Hydrate admin-editable state averages + APRs over the
  // static defaults. Safe to fail silently — falls back to lib/states.js.
  useEffect(() => { hydrateStateOverrides(); }, []);

  // Iter-127 — Hydrate per-route SEO overrides. Safe + idempotent.
  useEffect(() => { hydrateSeoOverrides(); }, []);

  // Iter-127 — Web Vitals beacons. Idempotent across re-mounts; safely
  // no-ops if REACT_APP_BACKEND_URL is missing.
  useEffect(() => { initWebVitals(); captureAttribution(); }, []);

  // Iter-185.L — Cookie consent bootstrap. Registers gtag Consent
  // Mode v2 defaults (analytics/ad storage = denied) BEFORE any GA4
  // event fires, then applies the user's persisted choice (or denied
  // defaults) to PostHog + gtag. Idempotent.
  useEffect(() => { bootstrapConsent(); }, []);

  return (
    <HelmetProvider>
      <div className="App font-body">
        {/* A11y: WCAG 2.4.1 — keyboard / screen-reader users can jump past the
            header straight to page content. Visually hidden until focused. */}
        <a
          href="#main-content"
          className="sr-only focus:not-sr-only focus:fixed focus:top-3 focus:left-3 focus:z-[200] focus:bg-[#10B981] focus:text-[#0A2540] focus:font-bold focus:px-4 focus:py-2 focus:shadow-2xl"
        >
          Skip to main content
        </a>
        <BrowserRouter>
          <ScrollToTop />
          <RouteAnalytics />
          <AppRouter />
          <Toaster position="top-center" richColors />
        </BrowserRouter>
      </div>
    </HelmetProvider>
  );
}

export default App;
